import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Facebook, Instagram, Share2, Download, Link2, MessageCircle, X } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) =>
  new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
const formatMileage = (mileage) => new Intl.NumberFormat('pt-PT').format(mileage);

// The visual card that will be captured as image for Instagram
const CardTemplate = React.forwardRef(({ car }, ref) => {
  const mainImage = resolveImageUrl(car.images?.[0] || '');

  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        height: 1080,
        background: 'linear-gradient(180deg, #090A0F 0%, #12141D 100%)',
        fontFamily: "'Outfit', 'Segoe UI', sans-serif",
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top red accent line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: '#DC2626' }} />

      {/* Car Image */}
      <div style={{ width: '100%', height: 580, overflow: 'hidden', position: 'relative' }}>
        {mainImage && (
          <img
            src={mainImage}
            alt={`${car.brand} ${car.model}`}
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 180,
            background: 'linear-gradient(transparent, #12141D)',
          }}
        />
        {/* Status badge */}
        <div
          style={{
            position: 'absolute',
            top: 32,
            left: 32,
            padding: '8px 20px',
            background: car.status === 'Disponível' ? '#10B981' : car.status === 'Reservado' ? '#DC2626' : '#6B7280',
            color: 'white',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {car.status}
        </div>
      </div>

      {/* Content area */}
      <div style={{ padding: '0 48px', marginTop: -40, position: 'relative', zIndex: 2 }}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 16, color: '#a1a1aa', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 500, marginBottom: 4 }}>
            {car.brand}
          </div>
          <div style={{ fontSize: 48, fontWeight: 800, color: 'white', lineHeight: 1.1 }}>
            {car.model}
          </div>
        </div>

        <div style={{ fontSize: 42, fontWeight: 800, color: '#DC2626', marginBottom: 28 }}>
          {formatPrice(car.price)}
        </div>

        <div style={{ display: 'flex', gap: 40, marginBottom: 32 }}>
          {[
            { label: 'Ano', value: car.year },
            { label: 'Km', value: `${formatMileage(car.mileage)} km` },
            { label: 'Combustível', value: car.fuel },
            { label: 'Caixa', value: car.transmission },
          ].map((spec) => (
            <div key={spec.label}>
              <div style={{ fontSize: 13, color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>
                {spec.label}
              </div>
              <div style={{ fontSize: 20, color: 'white', fontWeight: 600 }}>
                {spec.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer branding */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '20px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(9,10,15,0.9)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/logo-icon.png" alt="ICARDETAIL" width={40} height={40} style={{ objectFit: 'contain' }} crossOrigin="anonymous" />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'white', letterSpacing: '0.02em' }}>
              ICARDET<span style={{ color: '#DC2626' }}>AI</span>L
            </div>
            <div style={{ fontSize: 10, color: '#71717a', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              Automóveis
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, color: 'white', fontWeight: 600 }}>919 714 805</div>
          <div style={{ fontSize: 12, color: '#71717a' }}>icardetail.pt</div>
        </div>
      </div>
    </div>
  );
});
CardTemplate.displayName = 'CardTemplate';

const ShareCarCard = ({ car, onClose }) => {
  const { settings } = useSettings();
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Use the OG endpoint URL for Facebook (crawlers will read OG tags from it)
  const carPageUrl = `${window.location.origin}/viatura/${car.id}`;
  const ogShareUrl = `${API_URL}/api/cars/${car.id}/og`;

  // Vehicle-specific WhatsApp message
  const whatsAppMsg = [
    `${car.brand} ${car.model} (${car.year})`,
    `${formatPrice(car.price)} | ${formatMileage(car.mileage)} km | ${car.fuel}`,
    '',
    `ICARDETAIL Automoveis`,
    settings.phone,
    carPageUrl
  ].join('\n');

  const generateImage = async () => {
    if (!cardRef.current) return null;
    setGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#090A0F',
        width: 1080,
        height: 1080,
      });
      return canvas;
    } catch (err) {
      console.error('Error generating image:', err);
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = async () => {
    const canvas = await generateImage();
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `ICARDETAIL_${car.brand}_${car.model}_${car.year}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const shareFacebook = () => {
    // Facebook reads OG tags from the URL — use the OG endpoint which has proper meta tags and redirects to the page
    const url = encodeURIComponent(ogShareUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(whatsAppMsg);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(carPageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (clipErr) {
      console.warn('Clipboard API failed, using fallback:', clipErr.message);
      const input = document.createElement('input');
      input.value = carPageUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareNative = async () => {
    const canvas = await generateImage();
    if (navigator.share && canvas) {
      try {
        canvas.toBlob(async (blob) => {
          const file = new File([blob], `ICARDETAIL_${car.brand}_${car.model}.png`, { type: 'image/png' });
          await navigator.share({
            title: `${car.brand} ${car.model} (${car.year}) - ICARDETAIL`,
            text: whatsAppMsg,
            url: carPageUrl,
            files: [file],
          });
        });
      } catch (shareErr) {
        try {
          await navigator.share({
            title: `${car.brand} ${car.model} (${car.year}) - ICARDETAIL`,
            text: whatsAppMsg,
            url: carPageUrl,
          });
        } catch (cancelErr) { console.warn('Share cancelled:', cancelErr.message); }
      }
    } else if (navigator.share) {
      try {
        await navigator.share({
          title: `${car.brand} ${car.model} (${car.year}) - ICARDETAIL`,
          text: whatsAppMsg,
          url: carPageUrl,
        });
      } catch (cancelErr) { console.warn('Share cancelled:', cancelErr.message); }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#12141D] border border-zinc-800 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-white">Partilhar Viatura</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors" data-testid="share-modal-close">
            <X size={22} />
          </button>
        </div>

        {/* Card Preview */}
        <div className="p-5">
          <div className="border border-zinc-700 overflow-hidden mb-5" style={{ maxHeight: 320, overflow: 'hidden' }}>
            <div style={{ transform: 'scale(0.3)', transformOrigin: 'top left', width: 1080, height: 1080 }}>
              <CardTemplate ref={cardRef} car={car} />
            </div>
          </div>

          {/* Vehicle info summary */}
          <div className="mb-4 px-1">
            <p className="text-white font-medium">{car.brand} {car.model} ({car.year})</p>
            <p className="text-red-500 font-semibold">{formatPrice(car.price)} &middot; {formatMileage(car.mileage)} km &middot; {car.fuel}</p>
          </div>

          {/* Share Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={shareFacebook}
              className="flex items-center justify-center gap-2 py-3 px-4 bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium transition-colors"
              data-testid="share-facebook-btn"
            >
              <Facebook size={20} />
              Facebook
            </button>

            <button
              onClick={shareWhatsApp}
              className="flex items-center justify-center gap-2 py-3 px-4 bg-[#25D366] hover:bg-[#20BD5A] text-white font-medium transition-colors"
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle size={20} />
              WhatsApp
            </button>

            <button
              onClick={downloadImage}
              disabled={generating}
              className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[#833AB4] via-[#C13584] to-[#E1306C] hover:opacity-90 text-white font-medium transition-opacity disabled:opacity-50"
              data-testid="share-instagram-btn"
            >
              <Instagram size={20} />
              {generating ? 'A gerar...' : 'Instagram'}
            </button>

            {navigator.share && (
              <button
                onClick={shareNative}
                className="flex items-center justify-center gap-2 py-3 px-4 bg-zinc-700 hover:bg-zinc-600 text-white font-medium transition-colors"
                data-testid="share-native-btn"
              >
                <Share2 size={20} />
                Mais opções
              </button>
            )}

            <button
              onClick={copyLink}
              className={`flex items-center justify-center gap-2 py-3 px-4 border font-medium transition-all ${
                copied ? 'border-green-500 text-green-400 bg-green-500/10' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white'
              }`}
              data-testid="share-copy-link-btn"
            >
              <Link2 size={20} />
              {copied ? 'Link copiado!' : 'Copiar link'}
            </button>

            <button
              onClick={downloadImage}
              disabled={generating}
              className="flex items-center justify-center gap-2 py-3 px-4 border border-red-600 text-red-500 hover:bg-red-600 hover:text-white font-medium transition-all disabled:opacity-50"
              data-testid="share-download-btn"
            >
              <Download size={20} />
              {generating ? 'A gerar...' : 'Descarregar'}
            </button>
          </div>

          <p className="text-zinc-500 text-xs mt-4 text-center">
            Para Instagram: descarregue a imagem e publique diretamente na app
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareCarCard;
